// Device catalog helpers for the patient-file Devices tab. The getproductsforcohort
// feed returns single products (by EAN) and partner-ref kits in two separate lists;
// we normalize both into one shape so no layer above the service boundary branches.

const P4_BASE = 'https://p4.withings.com/';

export interface DropshipmentProduct {
  id: number;
  model?: number;
  models?: string;
  color?: string;
  ean: string;
  uri_picture?: string;
  modified_picture?: number;
  modified?: number;
  required?: boolean;
}

// A kit/bundle: identified by partner_ref, named by the catalog (no local map).
// Note the picture timestamp key differs from a product's `modified_picture`.
export interface PackagingRule {
  id: number;
  partner_ref: string;
  name?: string;
  uri_picture?: string;
  modified?: number;
  modified_product_picture?: number;
}

export type CatalogItemKind = 'ean' | 'partner_ref';

export interface CatalogItem {
  // 'ean:…' | 'pr:…' — an EAN and a partner ref are both bare numeric strings, so the
  // prefix keeps the selection id unambiguous.
  key: string;
  kind: CatalogItemKind;
  ref: string; // the EAN or the partner ref, exactly as sent on the wire
  name: string;
  namedByCatalog: boolean; // false → the tile shows the raw ref caption
  uriPicture?: string;
  modified?: number;
  // Per-kind: a product's modified_picture, a packaging rule's modified_product_picture.
  modifiedPicture?: number;
}

// EAN → patient-facing device name. Unknown EANs render as the EAN + a placeholder icon.
export const DEVICE_NAMES: Record<string, string> = {
  3700546708664: 'Body Pro 2',
  3700546709968: 'BPM Pro 2',
  3700546710292: 'XL-Cuff',
  3700546705984: 'BPM Connect (Wi-Fi / BLE)',
};

export function deviceDisplayName(ean: string): string {
  return DEVICE_NAMES[ean] ?? ean;
}

export function isKnownDevice(ean: string): boolean {
  return ean in DEVICE_NAMES;
}

// EANs first, then partner refs — a stable order that keeps the single devices in
// their current grid position and appends kits. A row with no usable ref is dropped:
// it could not be ordered, so rendering a selectable tile for it would dead-end.
export function normalizeCohortCatalog(feed: {
  products?: DropshipmentProduct[];
  packaging_rules?: PackagingRule[];
}): CatalogItem[] {
  const eans: CatalogItem[] = (feed.products ?? [])
    .filter((p) => String(p.ean ?? ''))
    .map((p) => {
      const ref = String(p.ean);
      return {
        key: `ean:${ref}`,
        kind: 'ean' as const,
        ref,
        name: deviceDisplayName(ref),
        namedByCatalog: isKnownDevice(ref),
        uriPicture: p.uri_picture,
        modified: p.modified,
        modifiedPicture: p.modified_picture,
      };
    });

  const kits: CatalogItem[] = (feed.packaging_rules ?? [])
    .filter((r) => String(r.partner_ref ?? ''))
    .map((r) => {
      const ref = String(r.partner_ref);
      const name = (r.name ?? '').trim();
      return {
        key: `pr:${ref}`,
        kind: 'partner_ref',
        ref,
        name: name || `Kit ${ref}`,
        namedByCatalog: Boolean(name),
        uriPicture: r.uri_picture,
        modified: r.modified,
        modifiedPicture: r.modified_product_picture,
      };
    });

  return [...eans, ...kits];
}

// Split a selection into the two order params. A partner ref passed inside `products`
// is rejected by the platform, so getting this backwards ships nothing.
export function orderRefsFor(
  item: Pick<CatalogItem, 'kind' | 'ref'> | null | undefined,
): { products: string[]; partnerRefs: string[] } {
  if (!item?.ref) return { products: [], partnerRefs: [] };
  return item.kind === 'partner_ref'
    ? { products: [], partnerRefs: [item.ref] }
    : { products: [item.ref], partnerRefs: [] };
}

// Full CDN URL for a catalog picture; cache-buster is `<modified>-<modifiedPicture>`.
export function productImageUrl(
  item: Pick<CatalogItem, 'uriPicture' | 'modified' | 'modifiedPicture'>,
): string | null {
  if (!item.uriPicture) return null;
  const t = [item.modified, item.modifiedPicture].filter((n) => n != null).join('-');
  return `${P4_BASE}${item.uriPicture}${t ? `?t=${t}` : ''}`;
}

// A product inside a placed order (programorder.list). A partner-ref order may carry
// {partner_ref, name}; an expanded one carries EANs and/or bare models.
export interface OrderProductLike {
  ean?: string | number;
  model?: number;
  partner_ref?: string;
  name?: string;
}

// getdevice `model` → device name (canonical Withings model list). Attached
// devices carry only a model, no EAN. 17 kept as a Body Pro 2 alias observed
// in the products feed (not in the canonical list).
export const DEVICE_MODELS: Record<number, string> = {
  1: 'Withings WBS01',
  2: 'WS30',
  3: 'Kid Scale',
  4: 'Smart Body Analyzer',
  5: 'Body+',
  6: 'Body Cardio',
  7: 'Body',
  9: 'Body Pro',
  10: 'Body Scan',
  11: 'WBS10',
  12: 'WBS11',
  17: 'Body Pro 2',
  18: 'Body Comp',
  19: 'Body Scan 2',
  20: 'Body Fit',
  21: 'Smart Baby Monitor',
  22: 'Withings Home',
  41: 'Withings Blood Pressure Monitor V1',
  42: 'Withings Blood Pressure Monitor V2',
  43: 'Withings Blood Pressure Monitor V3',
  44: 'BPM Core',
  45: 'BPM Connect',
  46: 'BPM Connect Pro',
  47: 'BPM Pro 2',
  48: 'BPM Vision',
  51: 'Pulse',
  52: 'Activite',
  53: 'Activite (Pop, Steel)',
  54: 'Withings Go',
  55: 'Activite Steel HR',
  58: 'Pulse HR',
  59: 'Activite Steel HR Sport Edition',
  60: 'Aura Dock',
  61: 'Aura Sensor',
  63: 'Aura Sensor V2',
  70: 'Thermo',
  71: 'BeamO',
  90: 'Move',
  91: 'Move ECG',
  92: 'Move ECG',
  93: 'ScanWatch',
  94: 'ScanWatch 2',
  95: 'ScanWatch Light',
  100: 'WUP01',
  1051: 'iOS step tracker',
  1052: 'iOS step tracker',
  1053: 'Android step tracker',
  1054: 'Android step tracker',
  1055: 'GoogleFit tracker',
  1056: 'Samsung Health tracker',
  1057: 'HealthKit step iPhone tracker',
  1058: 'HealthKit step Apple Watch tracker',
  1059: 'HealthKit other step tracker',
  1060: 'Google Health Connect',
  1061: 'Iglucose glucometer',
  1062: 'Huawei tracker',
  1063: 'Lingo Sensor',
};

export function deviceModelName(model: number | null | undefined): string {
  return (model != null && DEVICE_MODELS[model]) || 'Withings device';
}

// Label an order line. Precedence mirrors the platform's own kit-name resolution;
// never renders "undefined" for a product that carries no EAN.
export function orderProductLabel(p: OrderProductLike | null | undefined): string {
  if (!p) return 'Device';
  const name = (p.name ?? '').trim();
  if (p.partner_ref && name) return name;
  const ean = p.ean != null ? String(p.ean) : '';
  const known = ean ? DEVICE_NAMES[ean] : undefined;
  if (known) return known;
  if (p.model != null) return deviceModelName(p.model);
  if (ean) return ean;
  return 'Device';
}
